
import React, { useState, useEffect } from 'react';
import { User, UserRole, Company, Tenant } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import Spinner from '../common/Spinner';

interface SuperAdminUserModalProps {
    userToEdit: User | null;
    tenants: Tenant[];
    onClose: () => void;
    onSave: (user: Omit<User, 'uid' | 'createdAt'>, password?: string) => Promise<void>;
}

const SuperAdminUserModal: React.FC<SuperAdminUserModalProps> = ({ userToEdit, tenants, onClose, onSave }) => {
    const [name, setName] = useState(userToEdit?.name || '');
    const [email, setEmail] = useState(userToEdit?.email || '');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState(userToEdit?.role || UserRole.TENANT_ADMIN);
    
    const [selectedTenantId, setSelectedTenantId] = useState(userToEdit?.tenantId || '');
    const [selectedCompanyId, setSelectedCompanyId] = useState(userToEdit?.companyId || '');
    
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    
    const [active, setActive] = useState(userToEdit?.active ?? true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isEditing = !!userToEdit;

    // Cargar empresas cada vez que cambia el Tenant seleccionado
    useEffect(() => {
        if (selectedTenantId) {
            const fetchCompanies = async () => {
                setLoadingCompanies(true);
                try {
                    const fetched = await firestoreService.getCompanies(selectedTenantId);
                    setCompanies(fetched);
                    // Si la empresa actual no pertenece al nuevo tenant, resetearla
                    if (!fetched.find(c => c.companyId === selectedCompanyId)) {
                        setSelectedCompanyId('');
                    }
                } catch (e) {
                    console.error("Error fetching companies for tenant", e);
                } finally {
                    setLoadingCompanies(false);
                }
            };
            fetchCompanies();
        } else {
            setCompanies([]);
            setSelectedCompanyId('');
        }
    }, [selectedTenantId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        
        if (!selectedTenantId) {
            setError("Debes seleccionar una Agencia.");
            return;
        }

        if (!isEditing && password.length < 6) {
            setError("La contraseña debe tener al menos 6 caracteres.");
            return;
        }

        setIsSubmitting(true);
        
        // Construir objeto base sin campos opcionales para evitar undefined
        const userData: any = { 
            name, 
            email, 
            role, 
            active, 
            tenantId: selectedTenantId
        };

        // Solo añadir companyId si existe y el rol es CLIENT_VIEWER
        if (role === UserRole.CLIENT_VIEWER && selectedCompanyId) {
            userData.companyId = selectedCompanyId;
        } else {
            userData.companyId = ""; // Usar string vacío en lugar de undefined para Firestore
        }

        try {
            await onSave(userData, isEditing ? undefined : password);
            onClose();
        } catch (err: any) {
            setError(err.message || "Ocurrió un error inesperado.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex justify-center items-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl shadow-2xl p-8 w-full max-w-md border border-gray-800 animate-fade-in-scale">
                <h2 className="text-2xl font-bold text-white mb-6">{isEditing ? 'Editar Perfil' : 'Crear Usuario Inquilino'}</h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Nombre Completo</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white outline-none focus:ring-2 focus:ring-blue-500" required />
                    </div>
                     <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-50 outline-none" required disabled={isEditing} />
                    </div>
                    
                    {!isEditing && (
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Contraseña</label>
                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white outline-none focus:ring-2 focus:ring-blue-500" required minLength={6} />
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 border-t border-gray-800 pt-4 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Asignar a Agencia (Tenant)</label>
                            <select 
                                value={selectedTenantId} 
                                onChange={(e) => setSelectedTenantId(e.target.value)} 
                                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white outline-none" 
                                required
                            >
                                <option value="">Selecciona Agencia...</option>
                                {tenants.map(t => <option key={t.tenantId} value={t.tenantId}>{t.name}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Rol en el Inquilino</label>
                            <select 
                                value={role} 
                                onChange={(e) => setRole(e.target.value as UserRole)} 
                                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white outline-none"
                            >
                                <option value={UserRole.TENANT_ADMIN}>Admin de Agencia (Dueño)</option>
                                <option value={UserRole.ANALYST}>Analista de Agencia</option>
                                <option value={UserRole.CLIENT_VIEWER}>Cliente Final (Solo Lectura)</option>
                            </select>
                        </div>

                        {role === UserRole.CLIENT_VIEWER && (
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Empresa Específica (Solo Clientes)</label>
                                {loadingCompanies ? <Spinner size="sm" /> : (
                                    <select 
                                        value={selectedCompanyId} 
                                        onChange={(e) => setSelectedCompanyId(e.target.value)} 
                                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white outline-none"
                                        required={role === UserRole.CLIENT_VIEWER}
                                    >
                                        <option value="">Selecciona Empresa...</option>
                                        {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                                    </select>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2 py-2">
                        <input type="checkbox" id="user-active" checked={active} onChange={(e) => setActive(e.target.checked)} className="h-5 w-5 bg-gray-800 border-gray-700 rounded" />
                        <label htmlFor="user-active" className="text-sm text-gray-300">Usuario Activo</label>
                    </div>

                    {error && <p className="text-red-400 text-sm bg-red-900/20 p-2 rounded">{error}</p>}

                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={onClose} className="px-5 py-2 bg-gray-700 text-white rounded-lg">Cancelar</button>
                        <button type="submit" disabled={isSubmitting} className="px-5 py-2 bg-blue-600 text-white rounded-lg font-bold shadow-lg shadow-blue-900/20">
                            {isSubmitting ? <Spinner size="sm" /> : (isEditing ? 'Guardar' : 'Crear Usuario')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
export default SuperAdminUserModal;
